import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import displayFont from "@fontsource/barlow-condensed/files/barlow-condensed-latin-800-normal.woff2?url";
import bodyFont from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { PrivacyPreferences } from "@/components/site/PrivacyPreferences";

function NotFoundComponent() {
  return (
    <div className="pv-page-hero flex min-h-screen items-center justify-center px-4">
      <div className="pv-surface-card max-w-md rounded-[2rem] p-8 text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="pv-button pv-button--primary inline-flex items-center justify-center px-4 py-2 text-sm"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="pv-page-hero flex min-h-screen items-center justify-center px-4">
      <div className="pv-surface-card max-w-md rounded-[2rem] p-8 text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="pv-button pv-button--primary inline-flex items-center justify-center px-4 py-2 text-sm"
          >
            Try again
          </button>
          <a
            href="/"
            className="pv-button pv-button--secondary inline-flex items-center justify-center px-4 py-2 text-sm"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Productos NFC + QR para negocios en Chile | Pyme Visible" },
      {
        name: "description",
        content:
          "Stands, tarjetas y llaveros NFC + QR para conectar clientes con Google, WhatsApp e Instagram. Configurados para tu negocio.",
      },
      { name: "author", content: "Pyme Visible" },
      { property: "og:site_name", content: "Pyme Visible" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "apple-mobile-web-app-title", content: "Pyme Visible" },
    ],
    links: [
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: displayFont,
        crossOrigin: "anonymous",
      },
      { rel: "preload", as: "font", type: "font/woff2", href: bodyFont, crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="es-CL">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  // Standalone transfer utilities have no analytics or marketing cookies.
  const esTransferencia = useRouterState({
    select: (state) =>
      ["/transferencia", "/transferencia-full-market"].includes(
        state.location.pathname.replace(/\/$/, ""),
      ),
  });
  return (
    <>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      {!esTransferencia && <PrivacyPreferences />}
    </>
  );
}
